import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, createIdempotencyKey } from "@anyseal/api-client";
import {
  Button as AntButton,
  Checkbox,
  Empty,
  Input,
  Select,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  type TableColumnsType,
} from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CreditCardOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import {
  Button,
  PageHeader,
  PageSection,
  RequestId,
  StatePanel,
  formatDate,
  formatMoney,
  formatNumber,
} from "@anyseal/ui";
import { portalClient } from "../api";
import { FormModal } from "../components/FormModal";
import { useI18n } from "../i18n";
import { statusLabel } from "../status";
import { projectPath, useWorkspace } from "../workspace";
import { WorkspaceGuard } from "../WorkspaceGuard";

interface Account {
  available_microunits: number;
  reserved_microunits: number;
  spent_microunits: number;
  debt_microunits: number;
  billing_preference: string;
  version: number;
  updated_at: string;
}

interface LedgerEntry {
  id: string;
  entry_type: string;
  amount_microunits: number;
  available_after_microunits: number;
  reason: string;
  created_at: string;
}

interface PaymentOrder {
  id: string;
  trade_number: string;
  purpose: string;
  provider: string;
  amount_minor: number;
  currency: string;
  credit_microunits: number;
  status: string;
  checkout_url?: string;
  created_at: string;
}

interface Subscription {
  id: string;
  plan_id?: string;
  source: string;
  status: string;
  credit_microunits: number;
  used_microunits: number;
  reserved_microunits: number;
  ends_at: string;
}

interface Invoice {
  id: string;
  request_number: string;
  title: string;
  amount_minor: number;
  currency: string;
  status: string;
  order_trade_numbers: string[];
  evidence_reference?: string;
  rejection_reason?: string;
  version: number;
  created_at: string;
}

interface PaymentMethod {
  provider: string;
  mode: string;
  public_config: Record<string, unknown>;
}

interface DataList<T> {
  data: T[];
}

function statusColor(status: string): "success" | "error" | "warning" | "processing" | "default" {
  const normalized = status.toLowerCase();
  if (normalized.includes("success") || normalized.includes("paid") || normalized === "active") return "success";
  if (normalized.includes("fail") || normalized.includes("reject") || normalized.includes("cancel") || normalized.includes("debt")) return "error";
  if (normalized.includes("pending") || normalized.includes("requested") || normalized.includes("reserved")) return "warning";
  return "default";
}

const ENTRY_TYPES: Record<string, { "zh-CN": string; en: string }> = {
  topup: { "zh-CN": "充值", en: "Top-up" },
  top_up: { "zh-CN": "充值", en: "Top-up" },
  settlement: { "zh-CN": "请求结算", en: "Settlement" },
  refund: { "zh-CN": "退款", en: "Refund" },
  adjustment: { "zh-CN": "人工调整", en: "Adjustment" },
  grant: { "zh-CN": "发放", en: "Grant" },
  checkin: { "zh-CN": "签到奖励", en: "Check-in award" },
  referral: { "zh-CN": "推荐奖励", en: "Referral award" },
  voucher: { "zh-CN": "兑换券", en: "Voucher" },
  subscription: { "zh-CN": "订阅", en: "Subscription" },
};

function entryTypeLabel(value: string, locale: "zh-CN" | "en"): string {
  const known = ENTRY_TYPES[value.toLowerCase()];
  return known ? known[locale] : value.replace(/_/g, " ");
}

export function BillingPageInner() {
  const { locale } = useI18n();
  const { organization, project } = useWorkspace();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("ledger");
  const [showTopUp, setShowTopUp] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [amount, setAmount] = useState("100");
  const [provider, setProvider] = useState("");
  const [invoiceForm, setInvoiceForm] = useState({ title: "", billing_profile_reference: "", payment_order_ids: [] as string[] });
  const base = organization && project ? `${projectPath(organization.id, project.id)}/billing` : "";
  const isChinese = locale === "zh-CN";
  const currency = organization?.default_currency ?? "CNY";

  const account = useQuery({ queryKey: ["portal", "billing-account", base], queryFn: () => portalClient.get<Account>(`${base}/account`), enabled: Boolean(base) });
  const ledger = useQuery({ queryKey: ["portal", "billing-ledger", base], queryFn: () => portalClient.get<DataList<LedgerEntry>>(`${base}/ledger?limit=100`), enabled: Boolean(base) });
  const orders = useQuery({ queryKey: ["portal", "billing-orders", base], queryFn: () => portalClient.get<DataList<PaymentOrder>>(`${base}/orders?limit=50`), enabled: Boolean(base) });
  const subscriptions = useQuery({ queryKey: ["portal", "billing-subscriptions", base], queryFn: () => portalClient.get<DataList<Subscription>>(`${base}/subscriptions?all=true`), enabled: Boolean(base) });
  const invoices = useQuery({ queryKey: ["portal", "billing-invoices", base], queryFn: () => portalClient.get<DataList<Invoice>>(`${base}/invoices?limit=50`), enabled: Boolean(base) });
  const methods = useQuery({ queryKey: ["public", "payment-methods"], queryFn: () => portalClient.get<DataList<PaymentMethod>>("/api/public/v1/payment-methods", { authenticated: false }) });

  const enabledMethods = methods.data?.data ?? [];
  const selectedProvider = provider || enabledMethods[0]?.provider || "";
  const accountData = account.data;
  const unit = (value: number) => formatNumber(value / 1_000_000, locale);
  const availableOrders = orders.data?.data ?? [];
  const availableInvoices = invoices.data?.data ?? [];
  const invoiceableOrders = availableOrders.filter((order) => order.status === "succeeded" && !availableInvoices.some((invoice) => invoice.order_trade_numbers.includes(order.trade_number)));

  const topUp = useMutation({
    mutationFn: () => {
      const idempotencyKey = createIdempotencyKey();
      return portalClient.post<PaymentOrder>(`${base}/topups`, { provider: selectedProvider, amount_minor: Math.round(Number(amount) * 100), currency, idempotency_key: idempotencyKey }, { idempotencyKey });
    },
    onSuccess: (order) => {
      setShowTopUp(false);
      void queryClient.invalidateQueries({ queryKey: ["portal", "billing-orders", base] });
      if (order.checkout_url) window.location.assign(order.checkout_url);
    },
  });

  const preference = useMutation({
    mutationFn: (value: string) => portalClient.put<Account>(`${base}/preference`, { billing_preference: value, expected_version: accountData?.version }),
    onSuccess: (value) => queryClient.setQueryData(["portal", "billing-account", base], value),
  });

  const createInvoice = useMutation({
    mutationFn: () => {
      const idempotencyKey = createIdempotencyKey();
      return portalClient.post<Invoice>(`${base}/invoices`, { ...invoiceForm, idempotency_key: idempotencyKey }, { idempotencyKey });
    },
    onSuccess: () => {
      setShowInvoice(false);
      setInvoiceForm({ title: "", billing_profile_reference: "", payment_order_ids: [] });
      void invoices.refetch();
    },
  });

  const cancelInvoice = useMutation({ mutationFn: (invoice: Invoice) => portalClient.post<Invoice>(`${base}/invoices/${invoice.id}/cancel`, { expected_version: invoice.version }), onSuccess: () => void invoices.refetch() });
  const failure = account.error ?? ledger.error ?? orders.error ?? subscriptions.error ?? invoices.error;

  const refreshAll = () => {
    void account.refetch();
    void ledger.refetch();
    void orders.refetch();
    void subscriptions.refetch();
    void invoices.refetch();
    void methods.refetch();
  };

  const ledgerColumns: TableColumnsType<LedgerEntry> = [
    { title: isChinese ? "类型" : "Type", dataIndex: "entry_type", key: "entry_type", render: (value: string, row) => <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><span className={`portal-billing-entry-icon ${row.amount_microunits >= 0 ? "is-positive" : "is-negative"}`}>{row.amount_microunits >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}</span><strong>{entryTypeLabel(value, locale)}</strong></span> },
    { title: isChinese ? "原因" : "Reason", dataIndex: "reason", key: "reason", render: (value: string) => value || (isChinese ? "未填写" : "Not provided") },
    { title: isChinese ? "变动" : "Change", key: "amount", align: "right", render: (_, row) => <strong className={row.amount_microunits >= 0 ? "portal-billing-positive" : "portal-billing-negative"}>{row.amount_microunits >= 0 ? "+" : ""}{unit(row.amount_microunits)}</strong> },
    { title: isChinese ? "变更后可用" : "Balance after", key: "balance", align: "right", render: (_, row) => unit(row.available_after_microunits) },
    { title: isChinese ? "时间" : "Time", dataIndex: "created_at", key: "created_at", render: (value: string) => formatDate(value, locale) },
  ];

  const orderColumns: TableColumnsType<PaymentOrder> = [
    { title: isChinese ? "交易单号" : "Trade number", dataIndex: "trade_number", key: "trade_number", render: (value: string) => <Typography.Text code>{value}</Typography.Text> },
    { title: isChinese ? "用途" : "Purpose", dataIndex: "purpose", key: "purpose", render: (value: string) => value || "-" },
    { title: isChinese ? "金额" : "Amount", key: "amount", align: "right", render: (_, row) => formatMoney(row.amount_minor, row.currency, locale) },
    { title: isChinese ? "状态" : "Status", dataIndex: "status", key: "status", render: (value: string) => <Tag color={statusColor(value)}>{statusLabel(value, locale)}</Tag> },
    { title: isChinese ? "创建时间" : "Created", dataIndex: "created_at", key: "created_at", render: (value: string) => formatDate(value, locale) },
  ];

  const subscriptionColumns: TableColumnsType<Subscription> = [
    { title: isChinese ? "方案" : "Plan", key: "plan", render: (_, row) => <Typography.Text code>{row.plan_id || row.source}</Typography.Text> },
    { title: isChinese ? "状态" : "Status", dataIndex: "status", key: "status", render: (value: string) => <Tag color={statusColor(value)}>{statusLabel(value, locale)}</Tag> },
    { title: isChinese ? "已用 / 额度" : "Used / credit", key: "credit", align: "right", render: (_, row) => `${unit(row.used_microunits)} / ${unit(row.credit_microunits)}` },
    { title: isChinese ? "到期" : "Ends", dataIndex: "ends_at", key: "ends_at", render: (value: string) => formatDate(value, locale) },
  ];

  const invoiceColumns: TableColumnsType<Invoice> = [
    { title: isChinese ? "申请" : "Request", key: "request", render: (_, row) => <div><Typography.Text code>{row.request_number}</Typography.Text><span className="portal-billing-table-meta">{row.title || (isChinese ? "未填写" : "Not provided")}</span></div> },
    { title: isChinese ? "订单" : "Orders", dataIndex: "order_trade_numbers", key: "orders", render: (value: string[]) => value.join(", ") },
    { title: isChinese ? "金额" : "Amount", key: "amount", align: "right", render: (_, row) => formatMoney(row.amount_minor, row.currency, locale) },
    { title: isChinese ? "状态" : "Status", dataIndex: "status", key: "status", render: (value: string, row) => <div><Tag color={statusColor(value)}>{statusLabel(value, locale)}</Tag>{row.evidence_reference ? <span className="portal-billing-table-meta">{row.evidence_reference}</span> : row.rejection_reason ? <span className="portal-billing-table-meta">{row.rejection_reason}</span> : null}</div> },
    { title: isChinese ? "请求时间" : "Requested", dataIndex: "created_at", key: "created_at", render: (value: string) => formatDate(value, locale) },
    { title: isChinese ? "操作" : "Actions", key: "actions", align: "right", render: (_, row) => row.status === "requested" ? <AntButton size="small" type="link" danger loading={cancelInvoice.isPending} onClick={() => cancelInvoice.mutate(row)}>{isChinese ? "撤回" : "Cancel"}</AntButton> : null },
  ];

  const topUpAmount = Number(amount);
  const topUpValid = Number.isFinite(topUpAmount) && topUpAmount >= 1 && topUpAmount <= 100_000;
  const pageError = preference.error ?? cancelInvoice.error;

  const empty = (description: string) => <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={description} />;

  if (failure) {
    return (
      <section className="portal-page-grid">
        <StatePanel
          kind="error"
          title={isChinese ? "账单信息暂不可用" : "Billing information unavailable"}
          description={failure.message}
          requestId={failure instanceof ApiError && failure.requestId ? failure.requestId : undefined}
          action={<Button onClick={refreshAll}>{isChinese ? "重试" : "Retry"}</Button>}
        />
      </section>
    );
  }

  if (account.isLoading) {
    return (
      <section className="portal-page-grid">
        <StatePanel
          kind="loading"
          title={isChinese ? "正在加载账单与额度" : "Loading billing and credit"}
        />
      </section>
    );
  }


  return (
    <section className="portal-page-grid portal-billing-page">
      <PageHeader
        eyebrow={project?.display_name ?? "LonMax AI"}
        eyebrowIcon={<WalletOutlined style={{ marginRight: 5 }} />}
        title={isChinese ? "账单与额度" : "Billing & credit"}
        description={isChinese ? "所有额度变更都有完整链路，可追溯到请求、订单、订阅、退款或已审核的调整。" : "Every credit change is traceable to requests, orders, subscriptions, refunds, or reviewed adjustments."}
        actions={
          <>
            <Button
              variant="primary"
              onClick={() => setShowTopUp(true)}
              disabled={enabledMethods.length === 0}
            >
              <CreditCardOutlined style={{ marginRight: 6 }} />
              {isChinese ? "补充额度" : "Add credit"}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setShowInvoice(true)}
              disabled={invoiceableOrders.length === 0}
            >
              <FileTextOutlined style={{ marginRight: 6 }} />
              {isChinese ? "申请发票" : "Request invoice"}
            </Button>
            <Tooltip title={isChinese ? "刷新账单数据" : "Refresh billing data"}>
              <Button variant="secondary" onClick={refreshAll} aria-label={isChinese ? "刷新账单数据" : "Refresh billing data"}>
                <ReloadOutlined />
              </Button>
            </Tooltip>
          </>
        }
      />

      {/* Errors from non-modal mutations (preference change, invoice cancel)
          are surfaced here — silent failure on billing actions is not
          acceptable. */}
      {pageError ? (
        <div className="portal-form-error" role="alert">
          <strong>{isChinese ? "操作未完成" : "Action not completed"}</strong>
          <span>{pageError.message}</span>
          {pageError instanceof ApiError && pageError.requestId ? <RequestId value={pageError.requestId} /> : null}
        </div>
      ) : null}

      {/* Billing hero card (available credit + side stats) */}
      <div className="portal-billing-hero">
        <div className="portal-billing-hero__main">
          <div className="portal-billing-hero__kicker">
            <WalletOutlined />
            {isChinese ? "当前可用额度" : "Available credit"}
          </div>
          <div className="portal-billing-hero__amount">
            {unit(accountData?.available_microunits ?? 0)}
            <small>{currency}</small>
          </div>
          <div className="portal-billing-hero__meta">
            {isChinese ? "最后更新：" : "Updated: "}
            {accountData?.updated_at ? formatDate(accountData.updated_at, locale) : "-"}
            {enabledMethods.length === 0 ? ` · ${isChinese ? "支付通道未配置，暂不能充值" : "No payment provider configured"}` : ""}
          </div>
        </div>
        <div className="portal-billing-hero__side">
          <div className="portal-billing-stat">
            <div className="portal-billing-stat__label">{isChinese ? "冻结" : "Reserved"}</div>
            <div className="portal-billing-stat__value">{unit(accountData?.reserved_microunits ?? 0)}</div>
          </div>
          <div className="portal-billing-stat">
            <div className="portal-billing-stat__label">{isChinese ? "累计消耗" : "Total spent"}</div>
            <div className="portal-billing-stat__value">{unit(accountData?.spent_microunits ?? 0)}</div>
          </div>
          <div className="portal-billing-stat">
            <div className="portal-billing-stat__label">{isChinese ? "未结欠款" : "Outstanding debt"}</div>
            <div className={`portal-billing-stat__value${(accountData?.debt_microunits ?? 0) > 0 ? " portal-billing-stat__value--danger" : ""}`}>
              {unit(accountData?.debt_microunits ?? 0)}
            </div>
          </div>
        </div>
      </div>

      {/* Settings row */}
      <div className="portal-billing-settings">
        <PageSection
          title={<span><SafetyCertificateOutlined style={{ marginRight: 7 }} />{isChinese ? "扣费优先级" : "Charge priority"}<Tooltip title={isChinese ? "设置 API 请求优先消耗订阅额度或钱包余额。" : "Set whether API requests consume subscription credit or wallet balance first."}><InfoCircleOutlined style={{ marginLeft: 6, color: "var(--as-muted)" }} /></Tooltip></span>}
        >
          <Select
            value={accountData?.billing_preference ?? "subscription_first"}
            loading={preference.isPending}
            onChange={(value) => preference.mutate(value)}
            style={{ width: "100%", marginTop: 4 }}
            options={[
              { value: "subscription_first", label: isChinese ? "订阅优先" : "Subscription first" },
              { value: "wallet_first", label: isChinese ? "钱包优先" : "Wallet first" },
              { value: "wallet_only", label: isChinese ? "仅钱包" : "Wallet only" },
              { value: "subscription_only", label: isChinese ? "仅订阅" : "Subscription only" },
            ]}
          />
        </PageSection>
      </div>

      {/* Tabbed ledger */}
      <div className="portal-section-card portal-billing-ledger">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "ledger",
              label: isChinese ? "额度台账" : "Credit ledger",
              children: (
                <Table
                  rowKey="id"
                  loading={ledger.isLoading}
                  dataSource={ledger.data?.data ?? []}
                  columns={ledgerColumns}
                  scroll={{ x: 760 }}
                  pagination={{ pageSize: 10, showSizeChanger: false }}
                  locale={{ emptyText: empty(isChinese ? "暂无台账活动" : "No ledger activity") }}
                />
              ),
            },
            {
              key: "orders",
              label: isChinese ? "支付订单" : "Payment orders",
              children: (
                <Table
                  rowKey="id"
                  loading={orders.isLoading}
                  dataSource={availableOrders}
                  columns={orderColumns}
                  scroll={{ x: 760 }}
                  pagination={{ pageSize: 10, showSizeChanger: false }}
                  locale={{ emptyText: empty(isChinese ? "暂无支付订单" : "No payment orders") }}
                />
              ),
            },
            {
              key: "subscriptions",
              label: isChinese ? "订阅" : "Subscriptions",
              children: (
                <Table
                  rowKey="id"
                  loading={subscriptions.isLoading}
                  dataSource={subscriptions.data?.data ?? []}
                  columns={subscriptionColumns}
                  scroll={{ x: 760 }}
                  pagination={{ pageSize: 10, showSizeChanger: false }}
                  locale={{ emptyText: empty(isChinese ? "暂无订阅" : "No subscriptions") }}
                />
              ),
            },
            {
              key: "invoices",
              label: isChinese ? "发票" : "Invoices",
              children: (
                <Table
                  rowKey="id"
                  loading={invoices.isLoading}
                  dataSource={availableInvoices}
                  columns={invoiceColumns}
                  scroll={{ x: 880 }}
                  pagination={{ pageSize: 10, showSizeChanger: false }}
                  locale={{ emptyText: empty(isChinese ? "暂无发票申请" : "No invoice requests") }}
                />
              ),
            },
          ]}
        />
      </div>

      {/* Footer note */}
      <div className="portal-billing-footer-row">
        <span className="portal-billing-footer-note">
          <SafetyCertificateOutlined /> {isChinese ? "账务数据按项目隔离，所有金额使用精确的最小货币单位计算。" : "Billing data is isolated by project and monetary values use exact minor units."}
        </span>
        <Link to="/dashboard/growth" className="portal-inline-link">
          {isChinese ? "奖励与兑换" : "Rewards & redemption"}
        </Link>
      </div>

      {/* Top-up modal */}
      <FormModal
        open={showTopUp}
        title={isChinese ? "创建充值单" : "Create top-up order"}
        description={isChinese ? "提交后将跳转至对应支付机构，重试不会产生重复流水。" : "You will be redirected to the selected payment provider. Retries do not create duplicate ledger entries."}
        onCancel={() => setShowTopUp(false)}
        onSubmit={() => topUp.mutate()}
        submitLabel={isChinese ? "继续支付" : "Continue to payment"}
        busy={topUp.isPending}
        disabled={!selectedProvider || !topUpValid}
        error={topUp.error}
      >
        <label>
          <span>{isChinese ? "支付机构" : "Payment provider"}</span>
          <Select
            value={selectedProvider || undefined}
            onChange={setProvider}
            placeholder={isChinese ? "选择支付机构" : "Select a provider"}
            options={enabledMethods.map((item) => ({ value: item.provider, label: item.provider }))}
          />
        </label>
        <label>
          <span>{`${isChinese ? "金额" : "Amount"} (${currency})`}</span>
          <Input type="number" min={1} max={100000} step={0.01} value={amount} onChange={(event) => setAmount(event.target.value)} />
          <em className="portal-form-hint">
            {isChinese ? "最低 1，最高 100000；提交后跳转支付机构，重试不会重复入账。" : "Between 1 and 100,000. Retries do not create duplicate ledger entries."}
          </em>
        </label>
      </FormModal>

      {/* Invoice modal */}
      <FormModal
        open={showInvoice}
        title={isChinese ? "申请发票" : "Request invoice"}
        description={isChinese ? "本记录保存申请链路与开票证据；税务证据由结算方签发。" : "This records the request and issuance evidence; the settlement entity issues tax evidence."}
        onCancel={() => setShowInvoice(false)}
        onSubmit={() => createInvoice.mutate()}
        submitLabel={isChinese ? "提交申请" : "Submit request"}
        busy={createInvoice.isPending}
        disabled={!invoiceForm.title || !invoiceForm.billing_profile_reference || invoiceForm.payment_order_ids.length === 0}
        error={createInvoice.error}
        width={520}
      >
        <label>
          <span>{isChinese ? "发票抬头" : "Invoice title"}</span>
          <Input value={invoiceForm.title} onChange={(event) => setInvoiceForm({ ...invoiceForm, title: event.target.value })} maxLength={200} />
        </label>
        <label>
          <span>{isChinese ? "开票参考" : "Billing profile reference"}</span>
          <Input value={invoiceForm.billing_profile_reference} onChange={(event) => setInvoiceForm({ ...invoiceForm, billing_profile_reference: event.target.value })} maxLength={200} />
        </label>
        <div className="portal-form-modal__group" role="group" aria-labelledby="portal-invoice-orders-label">
          <span id="portal-invoice-orders-label">
            {isChinese ? "可开票订单" : "Paid orders"}
            <em>{isChinese ? "至少选择一个订单" : "Select at least one order"}</em>
          </span>
          <Checkbox.Group
            value={invoiceForm.payment_order_ids}
            onChange={(value) => setInvoiceForm({ ...invoiceForm, payment_order_ids: value as string[] })}
            options={invoiceableOrders.map((order) => ({ value: order.id, label: `${order.trade_number} · ${formatMoney(order.amount_minor, order.currency, locale)}` }))}
          />
        </div>
      </FormModal>
    </section>
  );
}

export function BillingPage() {
  return <WorkspaceGuard><BillingPageInner /></WorkspaceGuard>;
}
